// Number / sign helpers in the UI language (Intl). The sign is the true minus
// «−» in every language (the product's typography); grouping, decimal
// separators and the currency position follow the locale:
//   ru 48 200,96 $ · en $48,200.96 · de 48.200,96 $
import { locale } from '@/i18n';

const nfs = new Map<string, Intl.NumberFormat>();
const numFmt = (opts: Intl.NumberFormatOptions) => {
  const key = locale() + JSON.stringify(opts);
  let f = nfs.get(key);
  if (!f) { f = new Intl.NumberFormat(locale(), opts); nfs.set(key, f); }
  return f;
};

export const nf = (n: number, d = 0) => numFmt({ minimumFractionDigits: d, maximumFractionDigits: d }).format(Math.abs(n));
export const sign = (n: number) => (n > 0 ? '+' : n < 0 ? '−' : '');
/** +21,4% · +21.4% (locale-aware, with the sign) */
export const pct = (n: number, d = 1) => `${sign(n)}${numFmt({ minimumFractionDigits: d, maximumFractionDigits: d, style: 'percent' }).format(Math.abs(n) / 100)}`;
const cur = (n: number, d: number) => numFmt({ style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol', minimumFractionDigits: d, maximumFractionDigits: d }).format(Math.abs(n));
/** −$1 200 / $1,200 / 1.200 $ */
export const usd = (n: number, d = 0) => `${n < 0 ? '−' : ''}${cur(n, d)}`;
/** with an explicit sign: +$5 907 */
export const susd = (n: number, d = 0) => `${sign(n)}${cur(n, d)}`;
/**
 * A money amount split for big hero numbers: the symbol before or after (by
 * locale), the integer part and the cents. `$48 200` + `,96` (ru: `48 200` `,96` ` $`).
 */
export function moneyParts(n: number) {
  const ps = numFmt({ style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol', minimumFractionDigits: 2, maximumFractionDigits: 2 }).formatToParts(Math.abs(n));
  const i = ps.findIndex((p) => p.type === 'currency');
  const first = ps.findIndex((p) => p.type === 'integer');
  const pre = i < first ? '$' : '';
  const post = i > first ? ' $' : '';
  const int = ps.filter((p) => p.type === 'integer' || p.type === 'group').map((p) => p.value).join('');
  const frac = ps.filter((p) => p.type === 'decimal' || p.type === 'fraction').map((p) => p.value).join('');
  return { pre, int, frac, post };
}
/** a plan price: $49 · $16.90 · 16,90 $ (cents only when there are any) */
export const price = (n: number) => usd(n, Number.isInteger(n) ? 0 : 2);
export const dir = (n: number): '' | 'up' | 'down' => (n > 0 ? 'up' : n < 0 ? 'down' : '');
export const qty = (n: number) => numFmt({ maximumFractionDigits: 2 }).format(n);
export const cx = (...xs: Array<string | false | null | undefined>) => xs.filter(Boolean).join(' ');
